import { ApplicationElement, html } from '@effectivastudio/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';

import '@effectivastudio/lit-components/components/img_placeholder/element.js';
import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/label/element.js';
import '../origin/element.js';

import style from './style.lit.css';
import translations from './translations.js';

/**
 * Component that is used as custom groups header or as custom group item in a list
 *
 * @prop avatarOn - If true, renders avatar (optional)
 * @prop bgImage - Group's background image (optional)
 * @prop image - Group's avatar image (optional)
 * @prop layout - Predefined sizes (options: sm, lg) (optional)
 * @prop membersCount - Number of members (optional)
 * @prop region - Region name for the origin component (optional)
 * @prop regionIcon - Region icon for the origin component if region image is not defined (optional)
 * @prop regionImage - Region image for the origin component (optional)
 * @prop type - Type of the current group (options: , open, private, closed) (optional)
 * @prop url - The whole element will link to this URL (optional)
 * @prop featured - For 'Featured' label (optional)
 * @prop name - Internal property => Will be derived from the innerText of the element and will be passed as alt text for image in image placeholder
 *
 * @slot default - For title
 * @slot desc - For description (optional)
 *
 * @cssprop --img-height - Image and image placeholder height
 * @cssprop --main-padding - Component's padding
 * @cssprop --slot-padding - Padding around slots
 * @cssprop --title-font-size - Title font size in slot
 * @cssprop --description-font-size - Description font size in slot
 */

export default class GroupItem4 extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    avatarOn: { type: Boolean },
    bgImage: { type: String },
    image: { type: String },
    layout: { type: String, reflect: true },
    membersCount: { type: Number },
    region: { type: String },
    regionIcon: { type: String },
    regionImage: { type: String },
    type: { type: String },
    url: { type: String },
    featured: { type: Boolean },
    name: { state: true },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  static TYPE_ICON_NAMES = {
    closed: 'lock-keyhole',
    private: 'user-lock',
    open: 'unlock-keyhole',
  };

  constructor() {
    super();

    this.layout = 'sm';
  }

  /** @internal */
  get typeIcon() {
    return this.constructor.TYPE_ICON_NAMES[this.type];
  }

  onSlotChange() {
    this.name = this.innerText;
  }

  renderType() {
    return html`<ef-icon name=${this.typeIcon}></ef-icon>`;
  }

  renderMembersCount() {
    return html`
      <span class="members">
        ${this.t('members', { count: this.membersCount })}
      </span>
    `;
  }

  renderBgImage() {
    return html`<img src=${this.bgImage} alt=${ifDefined(this.name)} />`;
  }

  renderOrigin() {
    return html`
      <xs-origin
        .picture=${this.regionImage}
        .url=${this.regionUrl}
        .icon=${this.regionIcon}>
        <span>${this.region}</span>
      </xs-origin>
    `;
  }

  renderAvatar() {
    return html`
      <ef-img-placeholder
        picture=${ifDefined(this.image)}
        alt=${ifDefined(this.name)}>
      </ef-img-placeholder>
    `;
  }

  renderLabel() {
    return html` <ef-label layout="sm"> ${this.t('label')} </ef-label> `;
  }

  renderContent() {
    return html`
      <div class="main-wrapper">
        ${this.bgImage ? this.renderBgImage() : null}
        <div class="info">
          ${this.avatarOn ? this.renderAvatar() : null}
          ${this.type ? this.renderType() : null}
          ${this.membersCount ? this.renderMembersCount() : null}
          ${this.region ? this.renderOrigin() : null}
        </div>
        ${this.featured ? this.renderLabel() : null}
      </div>
      <div class="slot-wrapper">
        <slot @slotchange=${this.onSlotChange}></slot>
        <slot name="desc"></slot>
      </div>
    `;
  }

  renderWithLink() {
    return html` <a href=${this.url}>${this.renderContent()}</a> `;
  }

  render() {
    return html` ${this.url ? this.renderWithLink() : this.renderContent()} `;
  }
}

GroupItem4.registerTagName('xs-group-item-4');

customElements.get('xs-group-item-4') ||
  customElements.define('xs-group-item-4', GroupItem4);
